import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { FieldList, MetaBoxLayout, MetaTabs, PublishBox, type MetaTab } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { fetchDashboard } from '@/features/dashboard/api';
import { EventAttendanceTab } from '@/features/events/EventAttendanceTab';
import { EventNextSteps } from '@/features/events/EventNextSteps';
import { EventStudentTodo } from '@/features/events/EventStudentTodo';
import { EMPTY_EVENT_FORM, EventFormFields, capacityForApi, type EventFormValues } from '@/features/events/EventFormFields';
import { EventMeetTab } from '@/features/events/EventMeetTab';
import { EventDriveFilesBox } from '@/features/events/EventDriveFilesBox';
import { EventMeetingRecordsTab } from '@/features/events/EventMeetingRecordsTab';
import { EventMessagesTab } from '@/features/events/EventMessagesTab';
import { MeetingCandidateManager } from '@/features/events/MeetingCandidateManager';
import { MeetingSchedulePicker } from '@/features/events/MeetingSchedulePicker';
import { MeetLinkGuideButton } from '@/features/events/MeetLinkGuideModal';
import { EventNotificationsTab } from '@/features/events/EventNotificationsTab';
import { EventPhotosTab } from '@/features/events/EventPhotosTab';
import { EventStaffTab } from '@/features/events/EventStaffTab';
import { EventSurveysTab } from '@/features/events/EventSurveysTab';
import { EventTargetTab } from '@/features/events/EventTargetTab';
import {
  EVENT_STATUS_LABELS,
  PARTICIPATION_FORMAT_LABELS,
  changeEventStatus,
  copyEvent,
  fetchEvent,
  publishEvent,
  updateEvent,
  type EventStatus,
} from '@/features/events/api';
import { fetchEventTypes } from '@/features/masters/api';
import { eventSurveyTabs } from '@/features/events/eventSurveyTabs';
import type { EventSurveyKind } from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime, toDatetimeLocalValue } from '@/lib/datetime';

const SURVEY_ENABLED_FIELD: Record<
  EventSurveyKind,
  | 'pre_survey_enabled'
  | 'pre_assignment_enabled'
  | 'post_survey_enabled'
  | 'post_assignment_enabled'
  | 'day_survey_enabled'
  | 'director_survey_enabled'
> = {
  pre: 'pre_survey_enabled',
  assignment: 'pre_assignment_enabled',
  post: 'post_survey_enabled',
  post_assignment: 'post_assignment_enabled',
  day_survey: 'day_survey_enabled',
  director: 'director_survey_enabled',
};

const NEXT_STATUSES: Record<EventStatus, EventStatus[]> = {
  draft: [],
  published: ['closed', 'archived'],
  closed: ['archived'],
  archived: [],
};

/**
 * イベント詳細（指示書 §7-2「イベント詳細は9タブ」/ §12 M4・M5・M12）。
 * タブはAPI側の認可（§6）に合わせてロールごとに出し分ける。参照できないタブを
 * 表示して権限不足画面へ誘導するより、そもそも選べなくする方がわかりやすい。
 */
export function EventDetailPage(): ReactElement {
  const { eventId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // タブは #tabId を正とする（ブラウザの戻るで直前タブに戻れる / D-354）。
  // ダッシュボード等の既存直行リンク ?tab= も受け付ける
  const hashTab = location.hash.replace(/^#/, '') || null;
  const requestedTab = hashTab ?? searchParams.get('tab');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isOffice = user?.active_role === 'office';
  const isDirector = user?.active_role === 'director';
  const isStudent = user?.active_role === 'student';
  const isSuccess = user?.active_role === 'success';

  function goToTab(tabId: string): void {
    // ?tab= はダッシュボード直行用の互換。切替後は hash に寄せ、query の tab は消す
    // （概要へ戻したあと ?tab= が残ると再びそのタブが開いてしまう）
    const next = new URLSearchParams(location.search);
    next.delete('tab');
    const search = next.toString() ? `?${next.toString()}` : '';
    const hash = tabId === 'overview' ? '' : tabId;
    navigate({ pathname: location.pathname, search, hash }, { replace: false });
  }

  const query = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => fetchEvent(eventId),
    enabled: Boolean(eventId),
  });
  const eventTypes = useQuery({
    queryKey: ['event-types'],
    queryFn: fetchEventTypes,
    enabled: isOffice,
  });
  const dashboardQuery = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
    enabled: isOffice,
  });

  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [formValues, setFormValues] = useState<EventFormValues>(EMPTY_EVENT_FORM);
  const [statusDialog, setStatusDialog] = useState<EventStatus | null>(null);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [scheduleRevertConfirmOpen, setScheduleRevertConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (searchParams.get('copied') !== '1') return;
    setNotice(
      '前回の内容をコピーした下書きです。開催日時・対象者・課題の期限を確認してから公開してください。出欠・回答・席次・メッセージはコピーしていません。',
    );
    const next = new URLSearchParams(searchParams);
    next.delete('copied');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    if (forbidden) return <ForbiddenState />;
    if (notFound) return <ErrorState message="イベントが見つかりません。" />;
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const event = query.data;
  // 面談（サクセス面談・1on1）では出欠・席次・Meet資料を使わない（D-177）。
  // 1on1だけ事後アンケートを使う（D-271）
  const isMeeting = event.counterpart_role !== null;
  const isSuccessMeeting = event.counterpart_role === 'success';
  const isDirectorOneOnOne = event.counterpart_role === 'director';
  const isMonthly = event.type_code === 'monthly';
  // 概要編集: 事務局（サクセス面談はMeet URL・日程除く）／担当サクセスはMeet URLのみ（D-365）
  const canEditOverview = isOffice && event.status !== 'archived';
  const canEditMeetUrl = isSuccess && isSuccessMeeting && event.status !== 'archived';
  const editingMeetUrlOnly = editing && canEditMeetUrl && !isOffice;
  const pendingAbsenceCount = (dashboardQuery.data?.pending_event_actions ?? []).filter(
    (item) => item.event_id === eventId && item.kind === 'absence_pending',
  ).length;

  function startEdit(): void {
    setFormValues({
      type_id: event.type_id,
      title: event.title,
      description: event.description ?? '',
      starts_at: event.starts_at ? toDatetimeLocalValue(event.starts_at) : '',
      ends_at: event.ends_at ? toDatetimeLocalValue(event.ends_at) : '',
      venue: event.venue ?? '',
      meet_url: event.meet_url ?? '',
      capacity: event.capacity !== null ? String(event.capacity) : '',
      capacity_limited: event.capacity !== null,
      social_capacity: event.social_capacity !== null ? String(event.social_capacity) : '',
      attendance_required: event.attendance_required,
      attendance_deadline_at: event.attendance_deadline_at
        ? toDatetimeLocalValue(event.attendance_deadline_at)
        : '',
      has_social: event.has_social,
      participation_format: event.participation_format,
      meet_log_enabled: event.meet_log_enabled,
      pre_survey_enabled: event.pre_survey_enabled,
      pre_assignment_enabled: event.pre_assignment_enabled,
      post_survey_enabled: event.post_survey_enabled,
      post_assignment_enabled: event.post_assignment_enabled,
      day_survey_enabled: event.day_survey_enabled,
      director_survey_enabled: event.director_survey_enabled,
      member_id: '',
      counterpart_user_id: '',
    });
    setError(null);
    setEditing(true);
  }

  async function persistEdit(noticeText: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (editingMeetUrlOnly) {
        await updateEvent(eventId, { meet_url: formValues.meet_url || null });
      } else {
        const payload: Parameters<typeof updateEvent>[1] = {
          type_id: formValues.type_id,
          title: formValues.title,
          description: formValues.description || null,
          venue: formValues.venue || null,
          capacity: isMonthly ? null : capacityForApi(formValues),
          social_capacity: null,
          attendance_required: isMonthly ? false : formValues.attendance_required,
          attendance_deadline_at:
            !isMonthly && formValues.attendance_required && formValues.attendance_deadline_at
              ? datetimeLocalToIso(formValues.attendance_deadline_at)
              : null,
          has_social: false,
          participation_format: formValues.participation_format,
          meet_log_enabled: formValues.meet_log_enabled ?? false,
          pre_survey_enabled: formValues.pre_survey_enabled,
          pre_assignment_enabled: formValues.pre_assignment_enabled,
          post_survey_enabled: formValues.post_survey_enabled,
          post_assignment_enabled: formValues.post_assignment_enabled,
          day_survey_enabled: formValues.day_survey_enabled,
          director_survey_enabled: formValues.director_survey_enabled,
        };
        if (!(isSuccessMeeting && isOffice)) {
          payload.starts_at = formValues.starts_at
            ? datetimeLocalToIso(formValues.starts_at)
            : null;
          payload.ends_at = formValues.ends_at ? datetimeLocalToIso(formValues.ends_at) : null;
          payload.meet_url = formValues.meet_url || null;
        }
        await updateEvent(eventId, payload);
      }
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['notification-schedules', eventId] });
      setEditing(false);
      setScheduleRevertConfirmOpen(false);
      setNotice(noticeText);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSaveEdit(): Promise<void> {
    if (editingMeetUrlOnly) {
      await persistEdit('Meet URLを更新しました。');
      return;
    }
    const datetimeChanged =
      eventDateTimeChanged(event.starts_at, formValues.starts_at)
      || eventDateTimeChanged(event.ends_at, formValues.ends_at);
    if (datetimeChanged) {
      setScheduleRevertConfirmOpen(true);
      return;
    }
    await persistEdit('更新しました。');
  }

  async function onPublish(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await publishEvent(eventId);
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      await queryClient.invalidateQueries({ queryKey: ['event-target', eventId] });
      setPublishConfirmOpen(false);
      setNotice('イベントを公開しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '公開に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onSetSurveyKindEnabled(kind: EventSurveyKind, enabled: boolean): Promise<void> {
    setError(null);
    try {
      await updateEvent(eventId, { [SURVEY_ENABLED_FIELD[kind]]: enabled });
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
    }
  }

  async function onStatusChange(reason: string): Promise<void> {
    if (!statusDialog) return;
    setBusy(true);
    setError(null);
    try {
      await changeEventStatus(eventId, statusDialog, reason);
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      setStatusDialog(null);
      setNotice('ステータスを変更しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onCopy(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const copied = await copyEvent(eventId);
      setCopyConfirmOpen(false);
      navigate(`/events/${copied.id}?copied=1`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'コピーに失敗しました。');
      setCopyConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  }

  const overviewContent = editing ? (
    <div>
      <EventFormFields
        values={formValues}
        onChange={setFormValues}
        eventTypes={eventTypes.data ?? []}
        hideMeetUrlAndSchedule={isOffice && isSuccessMeeting}
        meetUrlOnly={editingMeetUrlOnly}
      />
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={
            busy ||
            (formValues.capacity_limited && !formValues.capacity.trim() && !isMonthly) ||
            (formValues.attendance_required &&
              !formValues.attendance_deadline_at &&
              event.counterpart_role == null &&
              !isMonthly)
          }
          onClick={() => void onSaveEdit()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          保存
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="btn-secondary"
        >
          キャンセル
        </button>
      </div>
    </div>
  ) : (
    <div>
      <FieldList
        fields={[
          { label: '種別', value: event.type_name },
          {
            label: '開催日時',
            value: event.starts_at
              ? `${formatDateTime(event.starts_at)}${
                  event.ends_at ? ` 〜 ${formatDateTime(event.ends_at)}` : ''
                }`
              : '日程調整中（決まったら「編集」から入力してください）',
          },
          { label: '会場', value: event.venue ?? '—' },
          ...(isMeeting
            ? []
            : [
                {
                  label: '参加形式',
                  value:
                    PARTICIPATION_FORMAT_LABELS[event.participation_format] ??
                    event.participation_format,
                },
              ]),
          { label: 'Meet URL', value: event.meet_url ?? '—' },
          ...(isSuccessMeeting && isSuccess && event.status !== 'draft'
            ? [
                {
                  label: 'Meetリンク案内',
                  value: (
                    <MeetLinkGuideButton eventId={eventId} meetUrl={event.meet_url} />
                  ),
                },
              ]
            : []),
          ...(isMeeting
            ? []
            : [
                {
                  label: '出欠',
                  value: event.attendance_required
                    ? event.attendance_deadline_at
                      ? `取る（回答期限: ${formatDateTime(event.attendance_deadline_at)}）`
                      : '取る'
                    : '取らない',
                },
                {
                  label: '参加ログ',
                  value: event.meet_log_enabled ? '取る' : '取らない',
                },
              ]),
          ...(!isMeeting && !isMonthly
            ? [
                {
                  label: '定員',
                  value:
                    event.capacity !== null
                      ? event.attending_count !== null
                        ? `${event.capacity}名（確定 ${event.attending_count}名${
                            event.capacity_full ? '・定員に達しています' : ''
                          }）`
                        : `${event.capacity}名`
                      : '設定しない',
                },
              ]
            : []),
          { label: '説明', value: event.description ?? '—' },
        ]}
      />
      {/* 編集: 事務局は概要（サクセス面談はMeet URL・日程除く）／担当サクセスはMeet URLのみ（D-365） */}
      {(canEditOverview || canEditMeetUrl) && event.status !== 'archived' && !editing && (
        <button
          type="button"
          onClick={startEdit}
          className="mt-4 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
        >
          {canEditMeetUrl && !isOffice ? 'Meet URLを編集' : '編集'}
        </button>
      )}
    </div>
  );

  // 運用タブと課題・アンケートタブを行で分ける（月例会などタブが多い / D-360）
  const surveyTabs: MetaTab[] =
    !isMeeting || isDirectorOneOnOne
      ? isDirectorOneOnOne
        ? [
            {
              id: 'surveys-post',
              label: '事後アンケート',
              content: (
                <EventSurveysTab
                  eventId={eventId}
                  eventTypeId={event.type_id}
                  kinds={['post']}
                  enabled={event.post_survey_enabled}
                  onSetEnabled={
                    isOffice ? (enabled) => void onSetSurveyKindEnabled('post', enabled) : undefined
                  }
                  emptyText="事後アンケートは割り当てられていません。"
                />
              ),
            },
          ]
        : eventSurveyTabs(event.type_code)
            .filter((tab) => tab.kind !== 'director' || isOffice || isDirector)
            .map((tab) => ({
              id: tab.tabId,
              label: tab.label,
              content: (
                <EventSurveysTab
                  eventId={eventId}
                  eventTypeId={event.type_id}
                  kinds={[tab.kind]}
                  enabled={event[tab.enabledField]}
                  onSetEnabled={
                    isOffice ? (enabled) => void onSetSurveyKindEnabled(tab.kind, enabled) : undefined
                  }
                  emptyText={tab.emptyText}
                />
              ),
            }))
      : [];

  const operationTabs: MetaTab[] = [
    { id: 'overview', label: '概要', content: overviewContent },
    ...(isOffice
      ? [
          {
            id: 'staff',
            label: '担当者',
            content: (
              <EventStaffTab eventId={eventId} counterpartRole={event.counterpart_role} />
            ),
          },
        ]
      : []),
    ...(isOffice
      ? [
          {
            id: 'target',
            label: '対象者',
            content: (
              <EventTargetTab
                eventId={eventId}
                isDraft={event.status === 'draft'}
                typeCode={event.type_code}
                surveyEnabled={{
                  pre: event.pre_survey_enabled,
                  assignment: event.pre_assignment_enabled,
                  post: event.post_survey_enabled,
                  post_assignment: event.post_assignment_enabled,
                  day_survey: event.day_survey_enabled,
                  director: event.director_survey_enabled,
                }}
              />
            ),
          },
        ]
      : []),
    ...(isMeeting === false &&
    !isMonthly &&
    (isStudent || isDirector || isSuccess || isOffice)
      ? [
          {
            id: 'attendance',
            label: '出欠',
            badge: isOffice ? pendingAbsenceCount : undefined,
            content: (
              <EventAttendanceTab
                eventId={eventId}
                hasSocial={false}
                isPublished={event.status !== 'draft'}
                attendanceRequired={event.attendance_required}
                attendanceDeadlineAt={event.attendance_deadline_at}
                participationFormat={event.participation_format}
                typeCode={event.type_code}
                capacity={event.capacity}
                capacityFull={event.capacity_full === true}
                attendingCount={event.attending_count}
              />
            ),
          },
        ]
      : []),
    ...(isOffice
      ? [
          {
            id: 'notifications',
            label: '通知',
            content: (
              <EventNotificationsTab
                eventId={eventId}
                notificationsSuppressed={event.notifications_suppressed}
              />
            ),
          },
        ]
      : []),
    ...(isMeeting === false && isOffice && event.meet_log_enabled
      ? [
          {
            id: 'meet-materials',
            label: 'Meet資料',
            content: <EventMeetTab eventId={eventId} />,
          },
        ]
      : []),
    ...(isMeeting
      ? []
      : [
          {
            id: 'photos',
            label: '当日写真',
            content: <EventPhotosTab eventId={eventId} />,
          },
        ]),
    ...(isMeeting &&
    event.counterpart_role === 'success' &&
    event.member_id !== null &&
    (isSuccess || isOffice)
      ? [
          {
            id: 'meeting-records',
            label: '面談記録',
            content: (
              <EventMeetingRecordsTab
                eventId={eventId}
                memberId={event.member_id}
                memberName={event.member_name}
                eventStartsAt={event.starts_at}
              />
            ),
          },
        ]
      : []),
    ...(isDirectorOneOnOne && (isDirector || isOffice)
      ? [
          {
            id: 'transcript',
            label: '文字起こし',
            content: <EventDriveFilesBox eventId={eventId} meetingMode />,
          },
        ]
      : []),
    {
      id: 'messages',
      label: 'グループメッセージ',
      content: <EventMessagesTab eventId={eventId} />,
    },
  ];

  const eventDetailTabs = [...operationTabs, ...surveyTabs];
  const eventDetailTabRows = [
    { ids: operationTabs.map((tab) => tab.id) },
    ...(surveyTabs.length > 0
      ? [{ heading: '課題・アンケート', ids: surveyTabs.map((tab) => tab.id) }]
      : []),
  ];

  return (
    <>
      <PageHeader
        title={event.title}
        description={event.type_name}
        extraParents={[
          { label: `${event.type_name}一覧`, to: `/events?type=${event.type_id}` },
        ]}
        actions={
          isOffice ? (
            <button
              type="button"
              onClick={() => setCopyConfirmOpen(true)}
              className="btn-secondary"
            >
              この内容で新しい下書きを作る
            </button>
          ) : undefined
        }
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {/* 作った直後に何が残っているかを示す（D-099） */}
      {isOffice && (
        <EventNextSteps
          eventId={eventId}
          isDraft={event.status === 'draft'}
          isMeeting={isMeeting}
          allowPostSurvey={isDirectorOneOnOne}
          typeCode={event.type_code}
          surveyEnabled={{
            pre: event.pre_survey_enabled,
            assignment: event.pre_assignment_enabled,
            post: event.post_survey_enabled,
            post_assignment: event.post_assignment_enabled,
            day_survey: event.day_survey_enabled,
            director: event.director_survey_enabled,
          }}
          onDisableKind={(kind) => void onSetSurveyKindEnabled(kind, false)}
          onGoToTab={goToTab}
          onPublish={() => setPublishConfirmOpen(true)}
          publishing={busy}
        />
      )}

      {/* 受講生: 日程未定の面談は、担当の実施可能日（候補日）から選んで確定する（D-183） */}
      {isStudent &&
        isMeeting &&
        event.starts_at === null &&
        event.status === 'published' && <MeetingSchedulePicker eventId={eventId} />}

      {/* 事務局: 1on1だけ候補日を代理登録（D-183）。サクセス面談は担当サクセスが実施可能日で扱う（D-365） */}
      {isOffice &&
        isDirectorOneOnOne &&
        event.starts_at === null &&
        event.status === 'published' &&
        event.counterpart_user_id !== null && (
          <MeetingCandidateManager
            eventId={eventId}
            counterpartUserId={event.counterpart_user_id}
            counterpartName={event.counterpart_name}
          />
        )}

      {/* 受講生にも「自分がこの回で何をすればよいか」を出す（D-179）。
          面談の出欠は使わない。1on1の事後アンケートだけ載せる（D-271） */}
      {isStudent && (!isMeeting || isDirectorOneOnOne) && event.status !== 'draft' && (
        <EventStudentTodo
          eventId={eventId}
          attendanceRequired={!isMeeting && !isMonthly && event.attendance_required}
          typeCode={event.type_code}
          capacity={event.capacity}
          capacityFull={event.capacity_full === true}
          onGoToTab={goToTab}
        />
      )}

      {/* 公開・ステータスはタブの上に横並びで置く（右サイドだとタブ幅を食う / D-350）。
          事務局のみ。理事・講師／サクセス担当には公開済みの回しか届かないため出さない（D-309） */}
      {isOffice && (
        <PublishBox
          layout="bar"
          statusLabel={EVENT_STATUS_LABELS[event.status]}
          updatedAt={formatDateTime(event.updated_at)}
          // 編集操作は概要タブ側（直す対象のすぐ下）に移した（D-163）。ここは
          // 公開・ステータス変更だけを扱う
          primaryLabel={event.status === 'draft' ? '公開する' : undefined}
          primaryDisabled={editing}
          onPrimary={
            !editing && event.status === 'draft'
              ? () => setPublishConfirmOpen(true)
              : undefined
          }
          extra={
            NEXT_STATUSES[event.status].length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-wptext-muted">ステータスの変更</span>
                {NEXT_STATUSES[event.status].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusDialog(s)}
                    className="btn-secondary px-2 py-1"
                  >
                    → {EVENT_STATUS_LABELS[s]}に変更
                  </button>
                ))}
              </div>
            ) : undefined
          }
        />
      )}

      <MetaBoxLayout
        main={
          <MetaTabs
            activeTabId={requestedTab ?? undefined}
            onTabChange={goToTab}
            tabs={eventDetailTabs}
            tabRows={eventDetailTabRows}
          />
        }
      />

      <ConfirmDialog
        open={scheduleRevertConfirmOpen}
        title="開催日時を変えますか？"
        message="案内メールの配信日時を開催日時に合わせて直します。日付だけ変えたときは同じ日数ずれます。時刻を変えたときは、通知タブで手作業で直した日時も自動計算に戻ります。"
        confirmLabel="日時を変える"
        onConfirm={() =>
          void persistEdit('更新しました。案内メールの配信日時も開催日時に合わせています。')
        }
        onCancel={() => setScheduleRevertConfirmOpen(false)}
      />
      <ConfirmDialog
        open={copyConfirmOpen}
        title="このイベントをコピーして下書きを作りますか？"
        message="案内文・会場・対象者の条件・担当者・課題/アンケートの割り当てを、新しい下書きとして複製します。出欠・回答・席次・当日写真・メッセージはコピーしません。開催日時もそのまま入るので、公開前に直してください。"
        confirmLabel="コピーして作る"
        onConfirm={() => void onCopy()}
        onCancel={() => setCopyConfirmOpen(false)}
      />
      <ConfirmDialog
        open={publishConfirmOpen}
        title="イベントを公開しますか？"
        message="いまの条件に当てはまる人を、このイベントの対象者として確定します（あとで会員の期やタグを変えても入れ替わりません）。受講生に公開され、出欠の受付が始まります。作成時に入っている案内メールは、公開後に時刻が来ると送られます（公開前はスキップされます）。"
        confirmLabel="公開する"
        onConfirm={() => void onPublish()}
        onCancel={() => setPublishConfirmOpen(false)}
      />
      <ConfirmDialog
        open={statusDialog !== null}
        title={statusDialog ? `ステータスを「${EVENT_STATUS_LABELS[statusDialog]}」に変更しますか？` : ''}
        requireReason
        confirmLabel="変更する"
        onConfirm={(reason) => void onStatusChange(reason)}
        onCancel={() => setStatusDialog(null)}
      />

      {isDirector && (
        <button
          type="button"
          onClick={() => navigate(`/events/${eventId}/evaluation`)}
          className="mt-4 mr-4 btn-secondary"
        >
          優秀者選定・態度フィードバックを入力
        </button>
      )}
      <button
        type="button"
        onClick={() => navigate(`/events?type=${event.type_id}`)}
        className="mt-4 text-wpaccent hover:underline"
      >
        {`← ${event.type_name}一覧に戻る`}
      </button>
    </>
  );
}

function eventDateTimeChanged(previousIso: string | null, nextLocal: string): boolean {
  if (!previousIso && !nextLocal) return false;
  if (!previousIso || !nextLocal) return true;
  return toDatetimeLocalValue(previousIso) !== nextLocal;
}
